import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Sparkles, RefreshCw, ChevronLeft } from "lucide-react";
import { AppModal, AppButton, AiModelPicker, reactSelectTheme } from "../ui";
import useAiModels from "../../hooks/useAiModels";
import apiClient from "../../utils/apiClient";
import { techStacksList } from "../../utils/common";

const MAX_QUESTIONS = 100;

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); GenerateAIQuestionsModal.scss
// deleted with it. Four colours in it belonged to nothing: the title icon was
// #8b5cf6 (a violet that is not --primary, so the AI modals were off-brand by a
// few degrees), "Change settings" was #3b82f6 (a blue, on a violet app), and the
// required asterisk and error text were #dc2626. `.gaiq-modal` was passed to
// AppModal and styled NOWHERE — the fourth dead class this phase.
const FIELD = "tw-flex tw-min-w-0 tw-flex-col tw-gap-[0.3rem]";
const LABEL = "tw-text-[0.78rem] tw-font-semibold tw-text-content";
const INPUT =
  "tw-w-full tw-rounded-sm tw-border tw-border-line tw-bg-[var(--input-bg)] " +
  "tw-px-[0.6rem] tw-py-[0.45rem] tw-text-[0.85rem] tw-text-content";
const ERROR =
  "tw-mt-[0.6rem] tw-rounded-[0.45rem] tw-border tw-border-danger/30 tw-bg-danger/[0.08] " +
  "tw-px-[0.6rem] tw-py-[0.45rem] tw-text-[0.8rem] tw-text-danger-fg";
const BACK =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-[0.15rem] tw-whitespace-nowrap " +
  "tw-border-0 tw-bg-transparent tw-p-0 tw-text-[0.78rem] tw-font-semibold tw-text-primary " +
  "hover:tw-underline";
const ITEM =
  "tw-flex tw-items-start tw-gap-[0.55rem] tw-rounded-[0.6rem] tw-border tw-border-line " +
  "tw-px-[0.7rem] tw-py-[0.6rem]";
const CHIP =
  "tw-inline-flex tw-items-center tw-gap-[0.2rem] tw-rounded-full tw-px-[0.45rem] " +
  "tw-py-[0.1rem] tw-text-[0.66rem] tw-font-semibold tw-capitalize";
// Difficulty is a status, so it reads the status families rather than three
// hand-mixed rgba tints — which is also what gives it a dark mode it never had.
const CHIP_TONE = {
  easy: "tw-bg-success-soft tw-text-success-fg",
  medium: "tw-bg-warning-soft tw-text-warning-fg",
  hard: "tw-bg-danger-soft tw-text-danger-fg",
};

const DIFFICULTIES = [
  { value: "", label: "Mixed (easy / medium / hard)" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const defaultTechStacks = techStacksList.filter((t) => t !== "");

// Shared, tokenised react-select styles — see ui/reactSelectTheme.js. This file used
// to carry its own copy hardcoding the v1 blue (#3b82f6).
const themeStyles = reactSelectTheme({ zIndex: "var(--z-popover)" });

/**
 * GenerateAIQuestionsModal — admin AI authoring for the AI (interview) Question
 * Bank. The admin picks a category (tech stack), optional focus topics, a
 * difficulty and how many; the AI writes open-ended interview questions (large
 * counts are batched + de-duplicated on the backend). Generated questions are
 * shown for review (uncheck the weak ones — preview only, nothing is saved yet)
 * and the keepers are persisted via the existing bulk-import endpoint.
 *
 * Props: onClose, onSaved(message)
 */
const GenerateAIQuestionsModal = ({ onClose, onSaved }) => {
  const [techStacks, setTechStacks] = useState(defaultTechStacks);
  // Was a read-only box showing the institute's configured chat model. That
  // model is tuned for answering one student's doubt; a hundred non-repeating
  // interview questions is a different job, so it is now a real choice.
  const ai = useAiModels("/ai/ai-questions/ai-models");
  const { quota, enabled: aiEnabled } = ai;

  const [form, setForm] = useState({ techStack: "", topics: "", difficulty: "", count: 20 });
  const [step, setStep] = useState("form"); // form | review
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState({ questions: [], model: "", requested: 0, generated: 0 });
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    apiClient
      .get("/ai/ai-questions/tech-stacks")
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data?.techStacks) ? data.techStacks : [];
        if (list.length) setTechStacks(list);
      })
      .catch(() => { /* keep defaults */ });
    return () => { alive = false; };
  }, []);

  const quotaBlockedMsg =
    quota && quota.allowed === false
      ? quota.reason === "quota_exhausted"
        ? "Your institute has used all its AI tokens for this month."
        : "AI generation is not available on your current plan."
      : "";

  const techStackOptions = (() => {
    const set = new Set(techStacks);
    if (form.techStack) set.add(form.techStack);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: t }));
  })();

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.techStack) {
      setError("Pick a category first.");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const { data } = await apiClient.post(
        "/ai/ai-questions/generate-ai",
        {
          techStack: form.techStack,
          topics: form.topics.trim(),
          difficulty: form.difficulty || undefined,
          count: Math.min(Math.max(Number(form.count) || 20, 1), MAX_QUESTIONS),
          ...ai.payload,
        },
        // A hundred questions on a reasoning model at high effort is minutes,
        // not the one minute this allowed before the model became a choice —
        // and a client-side abort bills the institute for questions nobody sees.
        { timeout: 420000 },
      );
      const questions = data.questions || [];
      setResult({
        questions,
        // What RAN, not what was picked — the server falls back when the
        // account cannot reach a model.
        model: data.model || ai.model,
        requested: data.requested || questions.length,
        generated: data.generated ?? questions.length,
      });
      setPicked(new Set(questions.map((_, i) => i)));
      setStep("review");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate questions.");
    }
    setGenerating(false);
  };

  const togglePick = (i) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const handleSave = async () => {
    const keep = result.questions.filter((_, i) => picked.has(i));
    if (!keep.length) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await apiClient.post("/ai/ai-questions/import", {
        questions: keep.map((q) => ({
          question: q.question,
          techStack: q.techStack || form.techStack,
          difficulty: q.difficulty,
        })),
      });
      const n = data.inserted ?? keep.length;
      onSaved(`${n} question${n === 1 ? "" : "s"} added${data.skipped ? `, ${data.skipped} skipped` : ""}.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save questions.");
      setSaving(false);
    }
  };

  const footer =
    step === "form" ? (
      <>
        <AppButton variant="outline" type="button" onClick={onClose} disabled={generating}>Cancel</AppButton>
        <AppButton
          variant="default"
          type="button"
          onClick={handleGenerate}
          disabled={generating || !aiEnabled || !form.techStack || !!quotaBlockedMsg}
        >
          {generating ? (<><RefreshCw size={14} className="tw-animate-spin" /> Generating…</>) : (<><Sparkles size={14} /> Generate</>)}
        </AppButton>
      </>
    ) : (
      <>
        <AppButton variant="outline" type="button" onClick={onClose} disabled={saving}>Discard</AppButton>
        <AppButton variant="default" type="button" onClick={handleSave} disabled={saving || picked.size === 0}>
          {saving ? "Saving…" : `Save ${picked.size} question${picked.size === 1 ? "" : "s"}`}
        </AppButton>
      </>
    );

  return (
    <AppModal
      show
      onHide={() => !generating && !saving && onClose()}
      size="xl"
      icon={<Sparkles size={18} className="tw-align-[-3px] tw-text-primary" />}
      title="Generate AI questions"
      footer={footer}
    >
      {step === "form" ? (
        <form onSubmit={handleGenerate} className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
            <div className={FIELD}>
              <label className={LABEL}>Category <span className="tw-text-danger">*</span></label>
              <Select
                classNamePrefix="react-select"
                options={techStackOptions}
                value={form.techStack ? { value: form.techStack, label: form.techStack } : null}
                onChange={(opt) => setForm((f) => ({ ...f, techStack: opt?.value || "" }))}
                placeholder="Search or select a category…"
                isClearable
                isSearchable
                styles={themeStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => "No matching category."}
              />
            </div>

            <div className={FIELD}>
              <label className={LABEL}>Difficulty</label>
              <select
                className={INPUT}
                value={form.difficulty}
                onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}
              >
                {DIFFICULTIES.map((d) => (
                  <option key={d.value || "mixed"} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            <div className={FIELD}>
              <label className={LABEL}>Number of questions</label>
              <input
                type="number"
                min="1"
                max={MAX_QUESTIONS}
                className={INPUT}
                value={form.count}
                onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
                required
              />
            </div>

            <div className="tw-col-span-full">
              <AiModelPicker {...ai} />
            </div>

            <div className={`${FIELD} tw-col-span-full`}>
              <label className={LABEL}>
                Topics to focus on <span className="tw-font-normal tw-text-content-subtle">(optional)</span>
              </label>
              <textarea
                className={`${INPUT} tw-resize-y`}
                rows={3}
                placeholder="e.g. hooks, context, performance, reconciliation — leave blank and the AI picks the key concepts of this category"
                value={form.topics}
                onChange={(e) => setForm((f) => ({ ...f, topics: e.target.value }))}
                maxLength={1000}
              />
            </div>
          </div>

          {quota && quota.allowed !== false && quota.limit > 0 && (
            <span className="tw-mt-[0.6rem] tw-block tw-rounded-[0.45rem] tw-bg-surface-sunken tw-px-[0.6rem] tw-py-[0.35rem] tw-text-[0.74rem] tw-text-content-muted">
              Institute AI tokens: {quota.used.toLocaleString()} / {quota.limit.toLocaleString()} used
              {" · "}{quota.remaining.toLocaleString()} left this month
            </span>
          )}

          <span className="tw-mt-2 tw-block tw-text-[0.76rem] tw-leading-[1.4] tw-text-content-muted">
            The AI writes open-ended interview questions on this category. Large batches are generated in
            parallel and de-duplicated against your existing bank — questions already in this category are
            skipped. You review everything before it's saved.
          </span>

          {quotaBlockedMsg && <div className={ERROR}>{quotaBlockedMsg}</div>}
          {error && <div className={ERROR}>{error}</div>}
        </form>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-[0.6rem]">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-[0.6rem] tw-text-[0.8rem] tw-text-content-muted">
            <span>
              {result.generated} generated by <strong>{result.model}</strong> · {picked.size} selected
              {result.generated < result.requested && (
                <span className="tw-text-content-subtle"> (asked for {result.requested})</span>
              )}
            </span>
            <button type="button" className={BACK} onClick={() => setStep("form")}>
              <ChevronLeft size={13} /> Change settings
            </button>
          </div>

          <ul className="tw-m-0 tw-flex tw-max-h-[52vh] tw-list-none tw-flex-col tw-gap-2 tw-overflow-y-auto tw-p-0">
            {result.questions.map((q, i) => (
              <li key={i} className={`${ITEM} ${picked.has(i) ? "" : "tw-opacity-50"}`}>
                <label className="tw-flex tw-items-center tw-pt-[0.1rem] [&_input]:tw-cursor-pointer">
                  <input
                    type="checkbox"
                    checked={picked.has(i)}
                    onChange={() => togglePick(i)}
                    aria-label="Keep this question"
                  />
                </label>
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[0.35rem]">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-[0.3rem]">
                    <span className={`${CHIP} ${CHIP_TONE[q.difficulty] || "tw-bg-surface-sunken tw-text-content-muted"}`}>{q.difficulty}</span>
                  </div>
                  <div className="tw-break-words tw-text-[0.88rem] tw-leading-[1.45] tw-text-content">{q.question}</div>
                </div>
              </li>
            ))}
          </ul>

          {error && <div className={ERROR}>{error}</div>}
        </div>
      )}
    </AppModal>
  );
};

export default GenerateAIQuestionsModal;
